/* eslint-disable arrow-body-style */
import {Actions, createEffect, ofType} from "@ngrx/effects";
import {inject} from "@angular/core";
import {Store} from "@ngrx/store";
import {catchError, exhaustMap, map, of} from "rxjs";
import {SellerPositionService} from "../../services/seller-position.service";
import {DealActions, SellerPositionActions} from "../deal.actions";
import {selectSelectedSourceDataset} from "../../../import/store/import.selectors";
import {Router} from "@angular/router";
import {tap} from "rxjs/operators";
import {ErrorHandlingUtil} from "../../../shared/utils/error-handling-util";
import {selectSelectedDealId} from "../deal/deal.selectors";
import {concatLatestFrom} from "@ngrx/operators";

export const loadAllSellerPositions = createEffect(
    (store$ = inject(Store), actions$ = inject(Actions), sellerPositionService = inject(SellerPositionService)) => {
        return actions$.pipe(
            ofType(SellerPositionActions.loadAll),
            exhaustMap((action) =>
                sellerPositionService.getSellerPositionsForDeal(action.dealId).pipe(
                    map((sellerPositions) => SellerPositionActions.loadAllSuccess({sellerPositions})),
                    catchError((errorResponse: any) => of(SellerPositionActions.loadError({errorMsg: ErrorHandlingUtil.getErrorMessage(errorResponse)})))
                )
            )
        );
    },
    {functional: true}
);

//TODO: return DealData here and then remove the sequential loadDealDataAfterSellerPosLoadAllSuccess effect
export const saveSellerPositions = createEffect(
    (store$ = inject(Store), actions$ = inject(Actions), sellerPositionService = inject(SellerPositionService)) => {
        return actions$.pipe(
            ofType(SellerPositionActions.saveAll),
            exhaustMap((action) => {
                if (action.dealId) {
                    return sellerPositionService.updateSellerPositions(action.dealId, action.sellerPositions).pipe(
                        map((sellerPositions) => SellerPositionActions.saveAllSuccess({sellerPositions})),
                        catchError((errorResponse: any) => of(SellerPositionActions.saveAllError({errorMsg: ErrorHandlingUtil.getErrorMessage(errorResponse)})))
                    );
                } else {
                    return of(SellerPositionActions.saveAllError({errorMsg: "Deal could not be found."}));
                }
            })
        );
    },
    {functional: true}
);

export const loadDealDataAfterSellerPosLoadAllSuccess = createEffect(
    (actions$ = inject(Actions), store$ = inject(Store)) => {
        return actions$.pipe(
            ofType(SellerPositionActions.saveAllSuccess),
            concatLatestFrom(() => store$.select(selectSelectedDealId)),
            map(([, dealId]) => {
                if (dealId === undefined) {
                    return DealActions.loadDealDataError({errorMsg: "Deal could not be found."})
                }
                return DealActions.loadDealData({dealId})
            })
        );
    },
    {functional: true}
);

export const loadSellerPositionsForDealAndFundValuation = createEffect(
    (actions$ = inject(Actions), sellerPositionService = inject(SellerPositionService)) => {
        return actions$.pipe(
            ofType(SellerPositionActions.load),
            exhaustMap((action) => {
                return sellerPositionService.getSellerPositionsForDealAndFundValuation(action.dealId, action.fundValuationId).pipe(
                    map((sellerPosition) => SellerPositionActions.loadSuccess({sellerPosition: sellerPosition})
                    ),
                    catchError((errorResponse: any) => of(SellerPositionActions.loadError({errorMsg: ErrorHandlingUtil.getErrorMessage(errorResponse)})))
                );
            })
        );
    },
    {functional: true}
);

export const saveSellerPosition = createEffect(
    (actions$ = inject(Actions), sellerPositionService = inject(SellerPositionService)) => {
        return actions$.pipe(
            ofType(SellerPositionActions.save),
            exhaustMap((action) => {
                if (!!action.dealId && !!action.fundValuationId) {
                    return sellerPositionService.save(action.dealId, action.fundValuationId, action.sellerPosition).pipe(
                        map((sellerPosition) => SellerPositionActions.saveLoadSuccess({sellerPosition})),
                        catchError((errorResponse: any) => of(SellerPositionActions.loadError({errorMsg: ErrorHandlingUtil.getErrorMessage(errorResponse)})))
                    );
                } else {
                    return of(SellerPositionActions.loadError({errorMsg: "Error while loading deal and/or fund valuation, please try again"}));
                }
            })
        );
    },
    {functional: true}
);

export const loadSellerPositionInDataset = createEffect(
    (actions$ = inject(Actions), store$ = inject(Store), sellerPositionService = inject(SellerPositionService)) => {
        return actions$.pipe(
            ofType(SellerPositionActions.loadDataset),
            concatLatestFrom(() => store$.select(selectSelectedSourceDataset)),
            exhaustMap(([action, dataset]) => {
                if (dataset !== "master") {
                    return sellerPositionService.getSellerPositionForFundValuation(action.fundValuationId, dataset).pipe(
                        map((sellerPositionAndDealId) => SellerPositionActions.loadDatasetSuccess({
                            sellerPosition: sellerPositionAndDealId.sellerPosition,
                            dealId: sellerPositionAndDealId.dealId
                        })),
                        catchError((errorResponse: any) => of(SellerPositionActions.loadError({errorMsg: ErrorHandlingUtil.getErrorMessage(errorResponse)}))));
                } else {
                    return of(SellerPositionActions.loadDatasetDoNothing());
                }
            })
        );
    },
    {functional: true}
);

export const navigateOnSuccess = createEffect(
    (actions$ = inject(Actions), router$ = inject(Router)) => {
        return actions$.pipe(
            ofType(SellerPositionActions.loadDatasetSuccess),
            tap((action) => router$.navigate([], {queryParams: {dealId: action.dealId}, queryParamsHandling: "merge"})));
    },
    {functional: true, dispatch: false}
);

